// ─────────────────────────────────────────────────────────────────────────────
// Caché de token a nivel app + dedupe de adquisiciones en vuelo.
//
// Motivo (telemetría App Insights `ai-postpulse-prd`, 2026-09-10): el landing dispara ~8 queries que
// llaman a `acquireTokenSilentSafe()` por separado. La ruta Teams
// (`microsoftTeams.authentication.getAuthToken()`) NO deduplica → ráfagas de `getAuthToken()`
// concurrentes, y si el token aún no está listo, ráfagas de cadena vacía → ráfagas de 401. El poll
// de 60s de `documentation-pills/for-me` (con `staleTime: 0`) vuelve a caer en ese hueco una y otra
// vez.
//
// Este módulo:
//   - Comparte UNA sola promesa en vuelo entre N llamadas concurrentes (dedupe).
//   - Cachea el último token válido y lo reutiliza mientras le queden > `SKEW_MS` para expirar.
//   - No cachea nada si el `exp` del token no es legible (no se puede saber cuándo caduca) ni si la
//     adquisición devolvió cadena vacía o lanzó.
//
// Aplica a AMBAS ramas (Teams y MSAL standalone). Para MSAL es en parte redundante con su caché
// interna, pero el dedupe de in-flight y evitar el hueco de renovación siguen ayudando; para Teams
// es todo ganancia.
// ─────────────────────────────────────────────────────────────────────────────

import { readJwtExp } from '@/auth/jwt';

/** Margen antes de `exp` a partir del cual se considera que el token hay que renovarlo. */
const SKEW_MS = 5 * 60 * 1000;

let inFlight: Promise<string> | null = null;
let cached: { token: string; expMs: number } | null = null;

/** Token cacheado si le quedan más de `SKEW_MS` para expirar; `null` en cualquier otro caso. */
export function getCachedToken(): string | null {
  if (!cached) return null;
  if (Date.now() >= cached.expMs - SKEW_MS) {
    cached = null;
    return null;
  }
  return cached.token;
}

/**
 * Ejecuta `acquire` deduplicando llamadas concurrentes y sirviendo de caché mientras el token siga
 * lejos de expirar. `acquire` es la adquisición real (Teams SSO o MSAL silencioso); si lanza
 * (p. ej. `SessionExpiredError` irrecuperable), el error se propaga a todos los que esperaban y no
 * se cachea nada.
 */
export async function acquireTokenDeduped(acquire: () => Promise<string>): Promise<string> {
  const fresh = getCachedToken();
  if (fresh) return fresh;

  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const token = await acquire();
      const exp = token ? readJwtExp(token) : null;
      cached = exp !== null ? { token, expMs: exp * 1000 } : null;
      return token;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** Reinicia caché y promesa en vuelo. Para tests y para el logout (que ya limpia la sesión aparte). */
export function resetTokenCache(): void {
  inFlight = null;
  cached = null;
}
